import { Modal } from 'bootstrap'
import Editor from 'ckeditor5-custom-build/ckeditor'
import { firstValueFrom, Subscription } from 'rxjs'
import { distinctUntilKeyChanged, filter, startWith } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

import { Component, OnDestroy, ViewChild } from '@angular/core'
import { EditorConfig } from '@ckeditor/ckeditor5-core'
import { FeatureProperty } from '@classes/FeatureProperty'
import { AuthenticationService } from '@services/authentication.service'
import { FeatureService } from '@services/feature.service'
import { ToastColor } from '@services/toast.service'
import { EditFeatureCustomFieldsComponent } from '@shared/edit-feature-custom-fields/edit-feature-custom-fields.component'
import { CreateCustomFieldCommand, CustomFieldCommand, DeleteCustomFieldCommand, UpdateCustomFieldCommand } from '@classes/CustomFieldCommand'
import { FeatureCustomField } from '@classes/Feature'
import { clearUploadCache, createDedupUploadAdapterPlugin } from '@utils/DedupUploadAdapter'

@Component({
  selector: 'shared-modal-edit-details',
  templateUrl: './edit-details.component.html',
  styleUrls: ['./edit-details.component.css'],
  standalone: false
})
export class EditDetailsComponent implements OnDestroy {
  @ViewChild('editFeatureCustomFieldsComponent') editFeatureCustomFieldsComponent: EditFeatureCustomFieldsComponent

  public cancelText = 'Close'
  public modal: Modal
  public submitColor: ToastColor = 'green'
  public submitText = 'Save'
  public title = 'Edit Details'

  public onCancel: CallableFunction

  private _subscriptions: Subscription[] = []
  public attachments: FeatureProperty[] = []
  public config: EditorConfig
  public currentTab: string = 'descriptionEditor'
  public editor = Editor as any
  public onSubmit: CallableFunction
  private descriptionEditorInstance: any | null = null
  private getToken = () => firstValueFrom(this.authenticationService.getAuthenticatedToken())

  private undoStack: CustomFieldCommand[] = []
  private redoStack: CustomFieldCommand[] = []

  public undoing: boolean = false
  public redoing: boolean = false

  get undoStackSize() { return this.undoStack.length }
  get redoStackSize() { return this.redoStack.length }

  get selectedFeature() {
    return this.featureService.selectedFeature
  }

  get selectedFeature$() {
    return this.featureService.selectedFeature$
      .pipe(startWith(this.selectedFeature))
  }

  constructor(
    public authenticationService: AuthenticationService,
    public featureService: FeatureService,
  ) {
    this._subscriptions.push(
      this.selectedFeature$.pipe(
        filter(feature => feature != null),
        distinctUntilKeyChanged('id')
      ).subscribe(feature => {
        clearUploadCache()

        this.configureUploadAdapter(feature.id)
      }),
    )
  }

  configureUploadAdapter(featureId: number) {
    const uploadAdapterPlugin = createDedupUploadAdapterPlugin(environment.api, featureId, this.getToken)

    this.config = {
      extraPlugins: [uploadAdapterPlugin],
    }

    if (this.descriptionEditorInstance) {
      uploadAdapterPlugin(this.descriptionEditorInstance)
    }
  }

  onDescriptionEditorReady(editor: any) {
    this.descriptionEditorInstance = editor

    if (this.selectedFeature) {
      this.configureUploadAdapter(this.selectedFeature.id)
    }
  }

  _submit(): boolean {
    this.onSubmit()
    return true
  }

  _cancel() {
    if (this.onCancel) {
      this.onCancel()
    }

    this.modal?.hide()
  }

  updateDetails(event: any) {
    const feature = this.selectedFeature
    const editorData = event.target.ckeditorInstance.getData()

    /** TODO: Fix issue where some files aren't deleted if pasted & removed without saving */
    const deleteUnusedEmbeddedImages = () => {
      // Match EFS file reference IDs from URLs like /api/v1/file/123
      const getFileReferenceIDs = (body: string) =>
        body.match(/(\/api\/v1\/file\/\d*)/g)?.map(url => parseInt(url.match(/\d+/g).pop()))
        ?? []
      // Match S3 reference IDs from URLs like /api/v1/s3/file/project/123/456 or /api/v1/s3/file-url?s3RefId=123
      const getS3ReferenceIDs = (body: string) => {
        const projectRouteIds = body.match(/\/api\/v1\/s3\/file\/project\/(\d+)\/\d+/g)
          ?.map(url => parseInt(url.match(/\/api\/v1\/s3\/file\/project\/(\d+)/)[1])) ?? []
        const queryParamIds = body.match(/s3RefId=(\d+)/g)
          ?.map(match => parseInt(match.replace('s3RefId=', ''))) ?? []
        return [...projectRouteIds, ...queryParamIds]
      }

      const currFileReferenceIDs = getFileReferenceIDs(editorData)
      const prevFileReferenceIDs = getFileReferenceIDs(feature.description)
      const currS3ReferenceIDs = getS3ReferenceIDs(editorData)
      const prevS3ReferenceIDs = getS3ReferenceIDs(feature.description)

      // Delete EFS embedded images that were removed
      prevFileReferenceIDs.filter(fileReferenceID => !currFileReferenceIDs.includes(fileReferenceID))
        .map(fileReferenceID => feature.embeddedImages.find(image => image.fileReferenceID == fileReferenceID))
        .filter(embeddedImage => embeddedImage != null)
        .forEach(embeddedImage => this.featureService.deleteFeatureProperty(embeddedImage).subscribe())

      // Delete S3 embedded images that were removed
      prevS3ReferenceIDs.filter(s3RefId => !currS3ReferenceIDs.includes(s3RefId))
        .map(s3RefId => feature.embeddedImages.find(image => image.s3ObjectReferenceId == s3RefId))
        .filter(embeddedImage => embeddedImage != null)
        .forEach(embeddedImage => this.featureService.deleteFeatureProperty(embeddedImage).subscribe())
    }

    deleteUnusedEmbeddedImages()

    feature.description = editorData

    this.featureService.updateFeature(feature).subscribe()
  }

  undo() {
    if (this.undoStack.length == 0) return
    if (this.undoing) return
    this.undoing = true

    const command = this.undoStack.pop()
    this.redoStack.push(command)
    command?.undo().subscribe(_ => this.undoing = false)
  }

  redo() {
    if (this.redoStack.length == 0) return
    if (this.redoing) return
    this.redoing = true

    const command = this.redoStack.pop()
    this.undoStack.push(command)
    command?.execute().subscribe(_ => this.redoing = false)
  }

  createCustomField(customField: FeatureCustomField) {
    const command = new CreateCustomFieldCommand(this.featureService, this.editFeatureCustomFieldsComponent, customField)
    this.executeCommand(command)
  }

  updateCustomField(original: FeatureCustomField, modified: FeatureCustomField) {
    const command = new UpdateCustomFieldCommand(this.featureService, this.editFeatureCustomFieldsComponent, original, modified)
    this.executeCommand(command)
  }

  deleteCustomField(customField: FeatureCustomField) {
    const command = new DeleteCustomFieldCommand(this.featureService, this.editFeatureCustomFieldsComponent, customField)
    this.executeCommand(command)
  }

  executeCommand(command: CustomFieldCommand) {
    this.undoStack.push(command)
    this.redoStack = []
    command.execute().subscribe()
  }

  ngOnDestroy(): void {
    this.descriptionEditorInstance = null
    this._subscriptions.forEach(sub => sub.unsubscribe())
  }
}
